/*
This code can be used to hijack GTM's `dataLayer.push` and defer the `push` by yielding in between.

As a result, the `push` events won't be part of the interaction's `processing duration` and INP should improve.
Do note that with this strategy, some events might never be sent to GTM, so double-check your event count before and after implementing this.

If you do see loss of events, you could add events to `const gtmPriorityEventMatches` array to handle with priority.
Do note that those events will then still continue to impact INP.
*/

// Import the DataLayerObject type from @gtm-support/core
import type { DataLayerObject } from '@gtm-support/vue-gtm'

type PostTaskPriority = 'user-blocking' | 'user-visible' | 'background'

type YieldOptions = {
  mark?: string
  priority?: PostTaskPriority
  event?: any[]
}

type PostTaskOptions = {
  priority?: PostTaskPriority
  signal?: AbortSignal
  delay?: number
}

type Scheduler = {
  yield(): Promise<void>
  postTask<T>(callback: () => T, options?: PostTaskOptions): Promise<T>
}

// Define GTM's dataLayer with custom properties
type GTMDataLayer = Array<DataLayerObject> & {
  push: (...items: DataLayerObject[]) => number
  originalPush?: (...items: DataLayerObject[]) => number
}

// Augment the Window interface using the matching DataLayerObject type
declare global {
  interface Window {
    dataLayer?: DataLayerObject[] // Using the type from @gtm-support/core
    yieldCounter?: Record<string, number>
    scheduler?: Scheduler
  }
}

// A set to track all unresolved yield promises
// based on https://kurtextrem.de/posts/improve-inp#exit-event-handlers-yieldunlessurgent
const pendingResolvers = new Set<() => void>()

// Resolves all unresolved yield promises when visibility changes
function resolvePendingPromises(): void {
  document.removeEventListener('visibilitychange', resolvePendingPromises)
  for (const resolve of pendingResolvers) {
    resolve()
  }
  pendingResolvers.clear()
}

/**
 * Yields to main thread before continuing execution.
 * If priority is 'user-blocking', it will asynchronously resolve in older browsers.
 * @param {object} options - see [https://github.com/WICG/scheduling-apis/blob/main/explainers/yield-and-continuation.md](spec)
 */
function yieldPolyfill(options: PostTaskOptions) {
  if ('scheduler' in window && window.scheduler?.yield) {
    return window.scheduler.yield()
  }

  if ('scheduler' in window && window.scheduler?.postTask) {
    return window.scheduler.postTask(() => {}, options)
  }

  // `setTimeout` could suffer from being delayed for longer - so for browsers not supporting yield,
  // we guarantee execution for high priority actions, but it doesn't yield as trade-off.
  if (options?.priority === 'user-blocking') return Promise.resolve()
  return new Promise((resolve) => setTimeout(resolve))
}

function yieldToMain(options: YieldOptions = {}): Promise<void> {
  let markName: string | undefined
  options.mark = options.mark || 'void'

  if (window.yieldCounter) {
    // Start counting + mark the start of yielding when yieldCounter exists
    const eventName = options.event?.[0]?.[1] || options.event?.[0]?.event || ''
    window.yieldCounter[options.mark] = window.yieldCounter[options.mark] || 0
    markName = [
      'yieldToMain',
      options.mark,
      ++window.yieldCounter[options.mark],
      eventName,
    ].join('-')
    performance.mark(markName + '-start')
  }

  const setMarkMeasure = (): void => {
    if (!markName) {
      return
    }
    performance.mark(markName + '-end')
    performance.measure(markName, {
      start: markName + '-start',
      end: markName + '-end',
      detail: {
        devtools: {
          dataType: 'track-entry',
          track: 'GTM',
          trackGroup: 'Yielding',
          color: 'secondary-light',
        },
      },
    })
  }

  const promise = new Promise<void>((resolve) => {
    pendingResolvers.add(resolve)

    // If the page is visible, proceed with yielding
    if (document.visibilityState === 'visible') {
      document.addEventListener('visibilitychange', resolvePendingPromises)

      const schedulerPromise = yieldPolyfill(options)

      schedulerPromise.then(() => {
        pendingResolvers.delete(resolve)
        resolve()
      })
    } else {
      resolvePendingPromises() // Resolve immediately if the document is hidden
    }
  })

  if (options.priority === 'user-blocking') {
    setMarkMeasure() // Mark the end of the task for user-blocking
    return Promise.resolve() // Return a resolved promise immediately
  }

  return promise.then(setMarkMeasure)
}

// Define the list of priority events using regular expressions
const gtmPriorityEventMatches = [
  /^FielmannBasic_EC_AddToCart$/,
  /^FielmannBasic_EC_AddToWishlist$/,
  /^FielmannBasic_EC_Purchase$/,
]

function isPriorityEvent(e: any[]): boolean {
  const eventName = e[0]?.[1] || e[0]?.event || ''
  if (typeof eventName !== 'string' || !eventName.length) {
    return false
  }

  // Check if the eventName matches any of the regular expressions in gtmPriorityEventMatches
  return gtmPriorityEventMatches.some((regex) => regex.test(eventName))
}

let hijacked = false

function yieldDataLayerPush(): void {
  // Check if dataLayer exists
  if (!window.dataLayer) {
    return
  }

  // log yielding to a custom track:
  window.yieldCounter = {}

  // Treat dataLayer as GTMDataLayer
  const dataLayer = window.dataLayer as GTMDataLayer

  // Store the original dataLayer.push function
  const originalPush = dataLayer.push

  // Add the originalPush property
  dataLayer.originalPush = originalPush

  // Override dataLayer.push
  dataLayer.push = function (...args: DataLayerObject[]): number {
    const e = args

    // Insert yield logic
    yieldToMain({
      mark: 'dataLayer',
      priority: isPriorityEvent(e) ? 'user-blocking' : 'background',
      event: e,
    }).then(() => {
      // Call the original dataLayer.push function with the arguments after yielding
      if (dataLayer.originalPush) {
        dataLayer.originalPush(...e)
      }
    })

    return e.length
  }
  hijacked = true
  console.log('DEBUG: Hijacked dataLayer.push')
}

export default defineNuxtPlugin((nuxt) => {
  if (import.meta.client) {
    const yieldObserver = new MutationObserver(() => {
      if (document.readyState === 'complete' && !hijacked) {
        // Abort and disconnect as dataLayer should have been created by now
        return yieldObserver.disconnect()
      }

      if (
        window.dataLayer &&
        (window.dataLayer as any).push !== Array.prototype.push
      ) {
        // Ensure dataLayer.push has been replaced by GTM
        yieldObserver.disconnect()
        yieldDataLayerPush() // Hijack dataLayer.push
      }
    })

    yieldObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
  }
})
